// src/forms/html_element_helpers/process_bad_response.js

import { return_error_clear_form_loading_state } from '../../forms/shared_form_ux_functions';
import {
    showRedBorderRequiredFieldsMissingValues,
    changeElementErrorStatus
} from '../../helpers/form_validation';
import {
    form_input_types_string,
    default_selectors,
    at_least_one_required_identifier_fields,
    phone_number_fields
} from '../../helpers/static_vars';

function clear_rejected_identifier_fields_from_cached_user_data(fields_to_clear, data_hub_instance = window?.data_hub) {
    fields_to_clear.forEach(field_name => {
        if (data_hub_instance?.user_data) {
            delete data_hub_instance.user_data[field_name];
        }
        if (typeof window !== 'undefined' && window.data_hub_user_data) {
            delete window.data_hub_user_data[field_name];
        }
    });
}

/**
 * Handle a non-2xx form-submit response from data-hub-action.
 *
 * data-hub-action returns a user-safe `error_message` (plus `error_code`) for every
 * structured error, so we echo that message straight to the toast — no client-side
 * error_code -> message map to maintain and drift out of sync with the server. Field
 * styling stays code-agnostic: red-border any blank required identifier inputs.
 *
 * @param {Response|Object} response  – fetch Response or already-parsed JSON
 * @param {HTMLElement} form_container
 * @param {string} error_container_selector – unused; kept for signature compatibility
 * @param {Object} data_hub_instance
 */
export async function process_bad_form_response(
    response,
    form_container = default_selectors.form_container_selector,
    error_container_selector = default_selectors.error_container_selector,
    data_hub_instance = window?.data_hub
) {
    // Parse JSON if it's a real fetch Response.
    let error_data = response;
    if (response instanceof Response) {
        try {
            error_data = await response.json();
        } catch (e) {
            return return_error_clear_form_loading_state(
                form_container,
                'process_bad_form_response: JSON parse failed',
                'Error submitting form. Please try again later.',
                data_hub_instance
            );
        }
    }

    // Echo the server's user-safe message; fall back to a generic one.
    const code = error_data?.error_code || 'form_submit_error';
    const safe_message = typeof error_data?.error_message === 'string'
        ? error_data.error_message.trim()
        : '';
    const userMessage = safe_message || 'Error submitting form. Please try again later.';

    if (code === 'bad_email_verify' || code === 'missing_email') {
        clear_rejected_identifier_fields_from_cached_user_data(['email'], data_hub_instance);
    }
    if (code === 'missing_phoneNumber') {
        clear_rejected_identifier_fields_from_cached_user_data(phone_number_fields, data_hub_instance);
    }

    // Code-agnostic field feedback: red-border any blank required identifier inputs.
    const allInputs = form_container.querySelectorAll(form_input_types_string);
    showRedBorderRequiredFieldsMissingValues(allInputs, at_least_one_required_identifier_fields);

    // Force a red border on the specific invalid-but-filled field(s). The blank-field
    // helper above only borders EMPTY required inputs, so a filled-but-rejected value
    // (e.g. a bad email or phone) needs this explicit pass. Targets fields by name/id
    // AND by input type (type="email" / type="tel"). Field ROUTING keyed by the stable
    // email/phone error codes — not user-facing wording (that comes from error_message).
    if (code === 'bad_email_verify' || code === 'missing_email') {
        form_container
            .querySelectorAll('input[name="email"], input#email, input[type="email"]')
            .forEach(el => changeElementErrorStatus(el, true));
    }
    if (code === 'missing_phoneNumber') {
        const phone_input_selectors = at_least_one_required_identifier_fields
            .filter(field_name => field_name.toLowerCase().includes('phone'))
            .flatMap(field_name => [`input[name="${field_name}"]`, `input#${field_name}`]);
        phone_input_selectors.push('input[type="tel"]');
        form_container
            .querySelectorAll(phone_input_selectors.join(', '))
            .forEach(el => changeElementErrorStatus(el, true));
    }

    // Tear down spinner/overlay & show the popover. internal = error_code (for
    // debugging/grouping), external = the user-facing message.
    return return_error_clear_form_loading_state(
        form_container,
        code,
        userMessage,
        data_hub_instance
    );
}
